/**
 * The enrichment queue: every article, and how far its web fiche has got.
 *
 * Three decisions worth reading.
 *
 * **The two numbers in the header are different numbers, and both are true.**
 * `total` counts the articles matching the filters; the list, when
 * "unpublishable only" is on, shows the subset that fails the gate. The filter
 * is applied in the domain because completeness is a rule with a policy in it,
 * so the honest header is "50 shown of 32 086" rather than one number that
 * quietly means neither.
 *
 * **Completion is a meter, not a percentage in text.** It answers "what should
 * I work on next", which is a comparison across rows, and a column of numbers
 * makes the reader do the comparison themselves.
 *
 * **Bulk publication is on this screen and nowhere else.** Mapping one family
 * makes several thousand articles publishable at once; the alternative to a
 * button here is 2 391 clicks. It reports what it refused, by criterion,
 * because that is the number somebody can act on.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useDebounced, useResource } from "@hardauto/core-data";
import {
  Badge,
  Button,
  Checkbox,
  DataTable,
  Input,
  Meter,
  Page,
  Select,
  Toolbar,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import { CatalogApi, type FicheRow, type WebCategory } from "../api";
import { completionTone, criterionLabelKey } from "../criteria";
import { NAMESPACE } from "../i18n";
import { CATALOG_PATHS } from "../paths";
import "./catalog.css";

const PAGE = 50;

export function FichesScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new CatalogApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayPublish = useCan("catalog.publish");

  const [search, setSearch] = useState("");
  const [categoryPath, setCategoryPath] = useState("");
  const [incompleteOnly, setIncompleteOnly] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  // Debounced so that typing "plaquette" is one query and not nine. The
  // supersession guard in `useResource` handles the other half: the list must
  // settle on the request matching the box, not on whichever finished last.
  const term = useDebounced(search, 300);

  const categoriesFetcher = useCallback(() => api.categoryList(false), [api]);
  const categories = useResource(categoriesFetcher);

  const fetcher = useCallback(
    () =>
      api.listFiches({
        search: term || undefined,
        categoryPath: categoryPath || undefined,
        incompleteOnly: incompleteOnly || undefined,
        publishedOnly: false,
        afterId: cursor,
        limit: PAGE,
      }),
    [api, term, categoryPath, incompleteOnly, cursor],
  );
  const fiches = useResource(fetcher);

  const rows = fiches.data?.items ?? [];
  const total = fiches.data?.total ?? 0;
  const filtering = Boolean(term || categoryPath || incompleteOnly);

  const columns: Column<FicheRow>[] = [
    {
      key: "reference",
      header: t("fiches.column.reference"),
      render: (row) => (
        <Link to={CATALOG_PATHS.fiche.replace("$id", row.article_id)}>
          <code>{row.reference}</code>
        </Link>
      ),
    },
    {
      key: "designation",
      header: t("fiches.column.designation"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <span>{row.web_designation || row.designation}</span>
          {/* Both, when they differ: the ERP designation is what an operator
              searches by, and the web one is what a customer reads. */}
          {row.web_designation ? (
            <span className="hardauto-cell__muted">{row.designation}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: "category",
      header: t("fiches.column.category"),
      render: (row) =>
        row.category_path ? (
          <span className="hardauto-cell__muted">{row.category_path}</span>
        ) : (
          <Badge tone="warning">{t("fiches.noCategory")}</Badge>
        ),
    },
    {
      key: "media",
      header: t("fiches.column.media"),
      render: (row) =>
        row.photo_count + row.document_count === 0 ? (
          <span className="hardauto-cell__muted">{t("fiches.noMedia")}</span>
        ) : (
          <span className="hardauto-cell__muted">
            {[
              row.photo_count > 0 ? t("fiches.photos", { count: row.photo_count }) : null,
              row.document_count > 0 ? t("fiches.documents", { count: row.document_count }) : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        ),
    },
    {
      key: "completion",
      header: t("fiches.column.completion"),
      render: (row) => (
        <Meter
          value={row.completion}
          label={t("fiches.column.completion")}
          tone={completionTone(row.completion, row.publishable)}
        />
      ),
    },
    {
      key: "status",
      header: t("fiches.column.status"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <Badge tone={row.status === "published" ? "success" : "neutral"}>
            {t(`fiches.status.${row.status}`, { defaultValue: row.status })}
          </Badge>
          {row.publishable ? null : (
            <span className="hardauto-cell__muted">
              {row.missing
                .slice(0, 2)
                .map((criterion) => t(criterionLabelKey(criterion), { defaultValue: criterion }))
                .join(", ")}
            </span>
          )}
        </div>
      ),
    },
  ];

  return (
    <Page
      title={t("fiches.title")}
      description={t("fiches.description")}
      eyebrow={
        fiches.data
          ? `${t("fiches.count", { count: total })} · ${t("fiches.shown", { count: rows.length })}`
          : undefined
      }
      actions={
        mayPublish ? (
          <Button
            variant="primary"
            busy={action.isBusy("bulk")}
            busyLabel={t("fiches.bulk.busy")}
            onClick={() => {
              void action.run("bulk", async () => {
                try {
                  const result = await api.publishMany({
                    search: term || undefined,
                    categoryPath: categoryPath || undefined,
                  });
                  if (result.published === 0) {
                    toast.show(t("fiches.bulk.none"), { tone: "info" });
                  } else {
                    const refused =
                      result.blocked > 0
                        ? ` ${t("fiches.bulk.blocked", { count: result.blocked })}`
                        : "";
                    toast.show(`${t("fiches.bulk.done", { count: result.published })}${refused}`, {
                      tone: "success",
                    });
                  }
                  if (result.next_cursor) toast.show(t("fiches.bulk.more"), { tone: "info" });
                  fiches.reload();
                } catch {
                  toast.show(t("error.title"), { tone: "danger" });
                }
              });
            }}
          >
            {t("fiches.bulk.action")}
          </Button>
        ) : undefined
      }
    >
      <Toolbar>
        <Input
          type="search"
          aria-label={t("fiches.searchLabel")}
          placeholder={t("fiches.searchPlaceholder")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            // A new filter is a new result set, so the cursor from the old one
            // would page into the middle of it.
            setCursor(undefined);
          }}
        />
        <label className="hardauto-toolbar__field">
          <span className="hardauto-visually-hidden">{t("fiches.categoryFilter")}</span>
          <Select
            value={categoryPath}
            onChange={(event) => {
              setCategoryPath(event.target.value);
              setCursor(undefined);
            }}
          >
            <option value="">{t("fiches.allCategories")}</option>
            {(categories.data?.items ?? []).map((category: WebCategory) => (
              <option key={category.id} value={category.path}>
                {/* The label, indented by depth — not the path. `path` is what
                    the API filters by and what the option carries as its
                    value; showing `materiel-d-atelier` to a reader who chose
                    "Matériel d'atelier" is showing them the plumbing. */}
                {`${"  ".repeat(Math.max(0, category.depth - 1))}${category.label}`}
              </option>
            ))}
          </Select>
        </label>
        <Checkbox
          label={t("fiches.incompleteOnly")}
          checked={incompleteOnly}
          onChange={(event) => {
            setIncompleteOnly(event.target.checked);
            setCursor(undefined);
          }}
        />
      </Toolbar>

      <DataTable<FicheRow>
        caption={t("fiches.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.article_id}
        loading={fiches.loading}
        loadingLabel={t("fiches.loading")}
        error={
          fiches.failed
            ? { title: t("error.title"), onRetry: fiches.reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={
          filtering
            ? { title: t("fiches.filtered.title"), description: t("fiches.filtered.body") }
            : { title: t("fiches.empty.title"), description: t("fiches.empty.body") }
        }
      />

      {/* Keyset pagination: "next" and "start again", not "page 7". The API
          pages by cursor because OFFSET at row 30 000 reads 30 000 rows to
          throw them away. */}
      <div className="hardauto-pager">
        <Button
          disabled={!fiches.data?.next_cursor}
          onClick={() => setCursor(fiches.data?.next_cursor ?? undefined)}
        >
          {t("fiches.next")}
        </Button>
        {cursor ? (
          <Button onClick={() => setCursor(undefined)}>{t("fiches.restart")}</Button>
        ) : null}
        <span className="hardauto-cell__muted">{total.toLocaleString(i18n.language)}</span>
      </div>
    </Page>
  );
}
